"use client";

import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { Star } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

const MODAL_CONTENT: Record<1 | 2 | 3, { headline: string; body: string }> = {
  1: {
    headline: "Wie gefällt dir Dialetics?",
    body: "Du bist seit Kurzem Mitglied bei Dialetics. Wir würden uns riesig über eine kurze Bewertung freuen!",
  },
  2: {
    headline: "Wie gefällt dir Dialetics?",
    body: "Du hast schon an mehreren Events teilgenommen. Wir würden uns riesig über eine kurze Bewertung freuen!",
  },
  3: {
    headline: "Deine Meinung zählt!",
    body: "Du bist ein aktives Mitglied unserer Community. Hilf anderen, Dialetics zu entdecken, mit einer kurzen Bewertung.",
  },
};

type ReviewPromptData = {
  reviewPrompt?: { eligible: boolean; trigger: 1 | 2 | 3 | null };
};

type Screen = "form" | "thanks";

/**
 * `sessionStorage` ist nicht immer da: Safari im privaten Modus und
 * blockierte Cookies lassen schon den Lesezugriff werfen. Ohne Speicher
 * gibt es keine Sperre, die etwas verbieten koennte — der Dialog wird
 * dann pro Seitenaufruf einmal gezeigt statt gar nicht.
 */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ohne Speicher bleibt die Sperre aus; das ist hinnehmbar.
  }
}

function removeSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // s. o.
  }
}

export function ReviewPromptModal() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<1 | 2 | 3 | null>(null);
  const [screen, setScreen] = useState<Screen>("form");
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Nur der erste Sterne-Klick zaehlt als "angenommen", spaetere Korrekturen nicht. */
  const trackedAccepted = useRef(false);

  /**
   * Der Nutzer hat sich auf den Prompt eingelassen, aber noch nicht abgeschickt.
   * Die Differenz zu `review_submitted` ist die Abbruchquote im Formular —
   * genau das wertet das Reviews-Dashboard aus.
   */
  const handleRate = (star: number) => {
    setRating(star);
    if (!trackedAccepted.current && trigger) {
      trackedAccepted.current = true;
      trackEvent("review_prompt_accepted", { trigger });
    }
  };

  const userId = user?.id;
  const pathname = usePathname();

  useEffect(() => {
    if (!userId) return;

    // Nur auf dem Dashboard. Die Komponente haengt in `app/providers.tsx` und
    // damit unter jeder Seite — ohne diese Zeile erschien der Dialog auch
    // dort, wo er stoert: Die April-Kohorte sah ihn direkt auf der
    // Erfolgsseite nach dem Kauf und gleich danach noch einmal auf dem
    // Dashboard, was 52 Anzeigen fuer 36 Menschen ergab.
    //
    // `pathname` steht in den Dependencies, damit der Wechsel von der
    // Erfolgsseite aufs Dashboard den Effekt erneut ausloest — beim
    // clientseitigen Navigieren bleibt die Komponente montiert.
    if (pathname !== "/dashboard") return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const maybeShow = () => {
      if (cancelled) return;

      // Ohne `sessionStorage` — Safari im privaten Modus, blockierte Cookies —
      // wirft schon der erste Zugriff. Frueher lag der ganze Block in einem
      // `setTimeout`, wo die Exception nur in der Konsole landete; jetzt haengt
      // eine Warteschleife davor, die sie weiterreichen wuerde.
      if (readSession("dialetics_modal_shown")) return;
      if (readSession("dialetics_review_prompt_shown")) return;

      const devTrigger = readSession("dialetics_dev_review_trigger");
      if (devTrigger) {
        removeSession("dialetics_dev_review_trigger");
        writeSession("dialetics_modal_shown", "1");
        writeSession("dialetics_review_prompt_shown", "1");
        const t = Number(devTrigger) as 1 | 2 | 3;
        setTrigger(t);
        setOpen(true);
        trackEvent("review_prompt_shown", { trigger: t, dev: true });
        return;
      }

      const handleData = (data: ReviewPromptData) => {
        if (!data.reviewPrompt?.eligible || !data.reviewPrompt.trigger) return;

        writeSession("dialetics_modal_shown", "1");
        writeSession("dialetics_review_prompt_shown", "1");
        setTrigger(data.reviewPrompt.trigger);
        setOpen(true);
        trackEvent("review_prompt_shown", { trigger: data.reviewPrompt.trigger });
      };

      // Der Cache stammt aus `VoucherExpiryModal`. Ist er defekt oder traegt
      // er kein `reviewPrompt`, wird frisch geladen statt still aufzugeben —
      // frueher stand das `return` im try-Block, sodass ein fehlgeschlagenes
      // `JSON.parse` den Dialog verschluckte.
      const cached = readSession("dialetics_membership_cache");
      if (cached) {
        try {
          const data = JSON.parse(cached) as ReviewPromptData;
          if (data.reviewPrompt) {
            handleData(data);
            return;
          }
        } catch {
          // Faellt auf den Netzabruf unten zurueck.
        }
      }

      fetch("/api/membership")
        .then((r) => r.json())
        .then(handleData)
        .catch(() => {});
    };

    /**
     * Der Gutschein-Hinweis hat Vorrang — er ist zeitkritisch, eine
     * Bewertung kann warten. Deshalb nicht einfach nach zwei Sekunden
     * anzeigen, sondern erst, wenn dessen Pruefung durch ist.
     *
     * Ohne dieses Warten entschiede das Netz: Dieser Dialog wird aus dem
     * Cache sofort bedient, der Gutschein-Hinweis braucht seinen Fetch.
     *
     * Nach spaetestens 5 Sekunden wird trotzdem angezeigt. Bleibt das Signal
     * aus — haengender Request, kein `sessionStorage`, Komponente gar nicht
     * gerendert —, soll der Dialog nicht dauerhaft verschwinden. Er kommt
     * dann drei Sekunden spaeter als sonst; das faellt weniger ins Gewicht
     * als ein Dialog, der nie erscheint.
     */
    const DEADLINE = Date.now() + 5000;

    const waitForVoucherCheck = () => {
      if (cancelled) return;

      const done = readSession("dialetics_voucher_check_done") !== null;

      if (done || Date.now() >= DEADLINE) {
        maybeShow();
        return;
      }
      pollTimer = setTimeout(waitForVoucherCheck, 150);
    };

    pollTimer = setTimeout(waitForVoucherCheck, 2000);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [userId, pathname]);

  useEffect(() => {
    if (screen !== "thanks") return;
    const timer = setTimeout(() => setOpen(false), 2500);
    return () => clearTimeout(timer);
  }, [screen]);

  const handleDismiss = () => {
    if (trigger) {
      fetch("/api/membership/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "dismiss", trigger }),
      }).catch(() => {});
      trackEvent("review_prompt_dismissed", { trigger });
    }
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/membership/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit",
          trigger,
          rating,
          comment: comment.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Ein Fehler ist aufgetreten.");
        setSubmitting(false);
        return;
      }

      trackEvent("review_submitted", {
        trigger,
        rating,
        hasComment: !!comment.trim(),
      });
      setScreen("thanks");
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!trigger) return null;
  const content = MODAL_CONTENT[trigger];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { if (screen === "form") handleDismiss(); else setOpen(false); } }}>
      <DialogContent>
        {screen === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle>{content.headline}</DialogTitle>
              <DialogDescription>{content.body}</DialogDescription>
            </DialogHeader>

            <div className="flex justify-center gap-1 pt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRate(star)}
                  onMouseEnter={() => setHovered(star)}
                  onMouseLeave={() => setHovered(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      star <= (hovered || rating)
                        ? "text-black fill-black"
                        : "text-gray-75"
                    }`}
                  />
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Was gefällt dir besonders? (optional)"
              rows={3}
              maxLength={2000}
              className="w-full rounded-xl border border-sand-50 bg-sand-10 px-4 py-3 text-copy-sm placeholder:text-gray-75 focus:outline-none focus:ring-2 focus:ring-sky-100/50 resize-none"
            />

            {error && (
              <p className="text-copy-sm text-red-100">{error}</p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={handleDismiss}>
                Später
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!rating || submitting}
              >
                {submitting ? "Wird gesendet…" : "Absenden"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-6 h-6 ${
                    star <= rating ? "text-black fill-black" : "text-gray-75"
                  }`}
                />
              ))}
            </div>
            <p className="text-headline-md text-black">Vielen Dank!</p>
            <p className="text-copy-sm text-gray-75 text-center">
              Deine Bewertung hilft anderen, Dialetics zu entdecken.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
